import React, { useEffect, useState } from 'react';
import { useLang } from '../../context/LanguageContext';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import PasswordInput from '../../components/PasswordInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { adminListUsers, adminUpdateUser, adminResetUserPassword, adminDeleteUser } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Mail, Shield, KeyRound, Trash2 } from 'lucide-react';

const AdminUsers = () => {
  const { lang } = useLang();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetUser, setResetUser] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListUsers();
      setUsers(data || []);
    } catch { toast({ title: 'Load failed', variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doDelete = async (u) => {
    const msg = lang === 'ar'
      ? `هل أنت متأكد من حذف حساب ${u.email}؟ هذا الإجراء لا يمكن التراجع عنه.`
      : `Delete account ${u.email}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await adminDeleteUser(u.user_id);
      toast({ title: lang === 'ar' ? '🗑️ تم حذف الحساب' : '🗑️ User deleted' });
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const toggleAdmin = async (u, value) => {
    try {
      await adminUpdateUser(u.user_id, { is_admin: value });
      toast({ title: value ? (lang === 'ar' ? 'تم منح الأدمن' : 'Granted admin') : (lang === 'ar' ? 'تم إزالة الأدمن' : 'Removed admin') });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const doReset = async () => {
    if (!resetUser || newPwd.length < 6) {
      toast({ title: lang === 'ar' ? 'كلمة المرور قصيرة' : 'Password too short (min 6)', variant: 'destructive' });      return;
    }
    setSaving(true);
    try {
      await adminResetUserPassword(resetUser.user_id, newPwd);
      toast({ title: lang === 'ar' ? 'تم تحديث كلمة المرور' : 'Password reset successfully', description: resetUser.email });
      setResetUser(null); setNewPwd('');
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'المستخدمون' : 'Users'}</h1>
        <p className="text-sm text-[#111827]/60 mt-2">{users.length} {lang === 'ar' ? 'مستخدم' : 'users'}</p>
      </div>

      {loading ? <div className="glass-card rounded-2xl p-10 text-center text-[#111827]/60">…</div>
      : (
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#2563eb]/8 border-b border-[#2563eb]/15">
                <tr className="text-[10px] uppercase tracking-widest text-[#2563eb]">
                  <th className="p-3 text-start">{lang === 'ar' ? 'الاسم' : 'Name'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'الإيميل' : 'Email'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'طريقة الدخول' : 'Provider'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'الطلبات' : 'Apps'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'تاريخ التسجيل' : 'Joined'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'أدمن' : 'Admin'}</th>
                  <th className="p-3 text-start">{lang === 'ar' ? 'إجراءات' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#2563eb]/8 hover:bg-[#2563eb]/4">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] flex items-center justify-center text-[#f8fafc] text-xs font-medium overflow-hidden">
                          {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : (u.name?.charAt(0)?.toUpperCase() || '?')}
                        </div>
                        <span className="text-[#0a0a0a]">{u.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-xs text-[#111827]/70">{u.email}</td>
                    <td className="p-3 text-xs text-[#2563eb] uppercase">{u.auth_provider}</td>
                    <td className="p-3 text-sm text-[#3b82f6] font-mono">{u.application_count}</td>
                    <td className="p-3 text-xs text-[#111827]/60">{u.created_at ? new Date(u.created_at).toISOString().split('T')[0] : '—'}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={u.is_admin_effective} onCheckedChange={(v) => toggleAdmin(u, v)} className="data-[state=checked]:bg-[#2563eb]" />
                        {u.is_admin_effective && <Shield className="w-3.5 h-3.5 text-[#3b82f6]" />}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {u.auth_provider === 'local' && (
                          <Button onClick={() => { setResetUser(u); setNewPwd(''); }} className="ghost-button rounded-md h-8 px-3 text-[10px] bg-transparent">
                            <KeyRound className="w-3 h-3 me-1" /> {lang === 'ar' ? 'إعادة كلمة المرور' : 'Reset Password'}
                          </Button>
                        )}
                        {!u.is_admin_effective && (
                          <Button onClick={() => doDelete(u)} className="rounded-md h-8 px-3 text-[10px] bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200" data-testid={`delete-user-${u.user_id}`} title={lang === 'ar' ? 'حذف الحساب نهائياً' : 'Delete account permanently'}>
                            <Trash2 className="w-3 h-3 me-1" /> {lang === 'ar' ? 'حذف' : 'Delete'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#0a0a0a] font-display text-xl">{lang === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}</DialogTitle>
            <div className="text-xs text-[#111827]/60">{resetUser?.email}</div>
          </DialogHeader>
          <div className="space-y-3">
            <PasswordInput placeholder={lang === 'ar' ? 'كلمة المرور الجديدة (6+ حرف)' : 'New password (min 6 chars)'} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="h-12 bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
            <p className="text-[10px] text-[#111827]/50">{lang === 'ar' ? 'سيتم تحديث كلمة المرور فوراً. شارك الجديدة مع المستخدم عبر قناة آمنة.' : 'Password will be updated immediately. Share the new password with the user through a secure channel.'}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetUser(null)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={doReset} disabled={saving || newPwd.length < 6} className="gold-button rounded-md h-10 px-5 text-xs">{saving ? '…' : (lang === 'ar' ? 'تحديث' : 'Update')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
